import type { KeyboardEvent } from "react";
import { useCallback, useState } from "react";

import type { IWebsitePageResult } from "pro-features/components/AutocompleteWebsites/types";
import { ANIMATION_DURATION_MS, ANIMATION_TIMING_FUNCTION } from "app/modules/project-from-comparison/config";
import { useProjectCreation } from "app/modules/project-from-comparison/context";
import projectCreatingTrack from "app/modules/project-from-comparison/tracking";
import type { DomainFromAutoselect } from "app/modules/project-from-comparison/types";

export const useItemActions = (domain: string, isPrimary: boolean, setIcon: (item: DomainFromAutoselect) => void) => {
    const { setDomains } = useProjectCreation();
    const [isEdit, setIsEdit] = useState(false);

    const onRemove = useCallback(() => {
        const element = document.querySelector<HTMLElement>(`[data-domain="${domain}"]`);

        if (element) {
            element.style.transform = "translateX(100%)";
            element.style.opacity = "0";
            element.style.transition = `transform ${ANIMATION_DURATION_MS}ms ${ANIMATION_TIMING_FUNCTION}, opacity ${ANIMATION_DURATION_MS}ms ${ANIMATION_TIMING_FUNCTION}`;

            setTimeout(() => {
                setDomains((prev) => prev.filter((d) => d !== domain));
            }, ANIMATION_DURATION_MS);
        } else {
            setDomains((prev) => prev.filter((d) => d !== domain));
        }

        projectCreatingTrack.trackRemoveDomainClick(isPrimary);
    }, [domain, isPrimary, setDomains]);

    const onEdit = useCallback(
        (selectedDomain: IWebsitePageResult) => {
            setDomains((prev) => {
                const index = prev.indexOf(domain);

                if (index === -1) {
                    return prev;
                }

                const newDomains = [...prev];
                newDomains[index] = selectedDomain.name;

                return newDomains;
            });
            setIcon({ name: selectedDomain.name, icon: selectedDomain.icon ?? "" });
            setIsEdit(false);
        },
        [domain, setDomains, setIcon],
    );

    const onMakePrimary = useCallback(() => {
        setDomains((prev) => {
            const currentIndex = prev.indexOf(domain);

            if (currentIndex === -1 || currentIndex === 0) {
                return prev;
            }

            const newDomains = [...prev];
            [newDomains[0], newDomains[currentIndex]] = [newDomains[currentIndex], newDomains[0]];

            return newDomains;
        });

        projectCreatingTrack.trackMakeNewPrimaryDomainClick();
    }, [domain, setDomains]);

    const onOpenAutocomplete = () => {
        setIsEdit(true);
        projectCreatingTrack.trackEditDomainClick();
    };

    const onItemKeyDown = (callback: () => void) => (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            callback();
        }
    };

    return { onEdit, onItemKeyDown, setIsEdit, onMakePrimary, onOpenAutocomplete, onRemove, isEdit };
};
